import { useState } from "react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import { Settings, Database, Shield, Globe, FolderOpen } from "lucide-react";
import ApplicationControlTab from "../Resources/tabs/ApplicationControlTab";
import DataAccessTab from "../Resources/tabs/DataAccessTab";
import PrivilegeManagementTab from "../Resources/tabs/PrivilegeManagementTab";
import URLControlTab from "../Resources/tabs/URLControlTab";
import EndpointActionButton from "../Endpoints/EndpointActionButton";
import PolicyProfileHeader from "./PolicyProfileHeader";
import "../Resources/EndpointResources.css";

const tabs = [
    { id: "app-control", name: "Application control", icon: Settings },
    { id: "data-access", name: "Data access", icon: Database },
    { id: "privilege-mgmt", name: "Privilege management", icon: Shield },
    { id: "url-control", name: "URL control", icon: Globe }
];

export default function PolicyProfilesResources() {
    const location = useLocation();
    const { id, os: osParam } = useParams();
    const os = osParam || 'windows';
    const [activeTab, setActiveTab] = useState("app-control");
    let profileName = location.state?.profileName;
    if (!profileName) {
        profileName = localStorage.getItem('currentProfileName') || "Unknown Profile";
    }
    const profileId = id ? Number(id) : -1;
    const navigate = useNavigate();

    const getOSType = (name: string) => {
        const lowerName = name.toLowerCase();
        if (lowerName.includes('windows')) return 'windows';
        if (lowerName.includes('linux')) return 'linux';
        if (lowerName.includes('mac')) return 'mac';
        return 'windows';
    };

    const osType = getOSType(profileName);

    const handleTabChange = (tabId: string) => {
        setActiveTab(tabId);
    };

    return (
        <div className="endpoint-detail-page policy-detail-page custom-scrollbar flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-x-hidden overflow-y-auto bg-background">
            <PolicyProfileHeader
                profileName={decodeURIComponent(profileName)}
                os={os}
                section="Resources"
                actions={(
                    <EndpointActionButton
                        onClick={() => navigate(
                            `/policyProfiles/categories/${profileId}/${os}/${osType}`,
                            { state: { profileName } }
                        )}
                        data-testid="button-view-categories"
                    >
                        <FolderOpen className="w-4 h-4" />
                        Categories
                    </EndpointActionButton>
                )}
            />

            <div className="endpoint-resources policy-detail-body">
                <div className="cea-tabs endpoint-resource-tabs" aria-label="Resource sections">
                    {tabs.map((tab) => (
                        <button
                            key={tab.id}
                            type="button"
                            onClick={() => handleTabChange(tab.id)}
                            className={`cea-tab ${activeTab === tab.id ? 'active' : ''}`}
                            aria-pressed={activeTab === tab.id}
                            data-testid={`tab-${tab.id}`}
                        >
                            <div className="flex items-center gap-2">
                                <tab.icon className="w-4 h-4" />
                                {tab.name}
                            </div>
                        </button>
                    ))}
                </div>

                <div className="endpoint-resource-content">
                    {activeTab === "app-control" && (
                        <ApplicationControlTab
                            selectedUser="system"
                            onUserSelect={() => { }}
                            users={[]}
                            device={null as any}
                            profileId={profileId}
                            os={os}
                            fromDetails={true}
                            showEmptyProperties
                        />
                    )}

                    {activeTab === "data-access" && (
                        <DataAccessTab
                            selectedUser="system"
                            onUserSelect={() => { }}
                            users={[]}
                            device={null as any}
                            profileId={profileId}
                            os={os}
                        />
                    )}

                    {activeTab === "privilege-mgmt" && (
                        <PrivilegeManagementTab
                            selectedUser="system"
                            onUserSelect={() => { }}
                            users={[]}
                            device={null as any}
                            profileId={profileId}
                            os={os}
                        />
                    )}

                    {activeTab === "url-control" && (
                        <URLControlTab
                            selectedUser="system"
                            onUserSelect={() => { }}
                            users={[]}
                            device={null as any}
                            profileId={profileId}
                            os={os}
                            fromDetails={true}
                        />
                    )}
                </div>
            </div>
        </div>
    );
}
